import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import AnimatedCounter from '../components/AnimatedCounter';
import { fadeUp, staggerContainer } from '../utils/animations';

/* ── Department cards ───────────────────────────────────── */
const DEPARTMENTS = [
  {
    icon: 'fa-solid fa-print',
    label: 'Flex Printing & Print Design',
    desc: 'Flex banners, visiting cards, brochures, stationery — designed and printed in-house at our Kalarcode studio in Alappuzha. Walk in for same-day printing.',
    to: '/print',
    color: '#8B5CF6',
    tags: ['Flex Printing', 'Visiting Cards', 'Brochures', 'Stationery'],
    focus: 'High-Impact Print Materials',
    deliverables: [
      'Heavy-Duty Outdoor UV Flex Banners',
      'Premium Laminated Visiting Cards',
      'Corporate Brochure & Flyer Printing',
      'Custom Brand Stationery & Stickers'
    ]
  },
  {
    icon: 'fa-solid fa-chart-line',
    label: 'Digital Marketing',
    desc: 'Local SEO, Google & Meta Ads, social media, and marketing automation that put your brand in front of the right audience.',
    to: '/services/digital-growth',
    color: '#0A8FDC',
    tags: ['SEO', 'Meta Ads', 'Google Ads', 'Social Media'],
    focus: 'Performance & ROI',
    deliverables: [
      'Google Maps & Local SEO Domination',
      'ROI-Focused Google & Meta Campaigns',
      'Auto Lead Routing to WhatsApp/CRM',
      'Detailed Competitor & Keyword Audits'
    ]
  },
  {
    icon: 'fa-solid fa-camera',
    label: 'Photography & Video',
    desc: 'Wedding coverage, event photography, live streaming, and cinematic brand films — captured by our in-house team.',
    to: '/photography',
    color: '#FF5C00',
    tags: ['Weddings', 'Events', 'Live Stream', 'Reels'],
    focus: 'Cinematic Storytelling',
    deliverables: [
      'Pre-Wedding & Cinematic Highlights',
      'Multi-Camera Live Event Streaming',
      'HD Corporate Events & Launch Coverage',
      'Scroll-Stopping Reels & Post-Production'
    ]
  },
  {
    icon: 'fa-solid fa-pen-nib',
    label: 'Branding & Identity',
    desc: 'Logo systems, brand guidelines, and visual identities crafted to position your business above the competition.',
    to: '/services/branding',
    color: '#10b981',
    tags: ['Logo Design', 'Brand Guide', 'Style System', 'Collateral'],
    focus: 'Visual Positioning',
    deliverables: [
      'Scalable Vector Logo Engineering',
      'Comprehensive Core Brand Guides',
      'Cohesive Social Media Asset Packs',
      'Print-Ready Marketing Materials'
    ]
  },
  {
    icon: 'fa-solid fa-code',
    label: 'Web Development',
    desc: 'Custom websites, eCommerce stores, and web apps built mobile-first with clean code and conversion in mind.',
    to: '/services/web-development',
    color: '#f59e0b',
    tags: ['Websites', 'eCommerce', 'SaaS', 'Automation'],
    focus: 'Modern Engineering',
    deliverables: [
      'Sub-2 Second Mobile Load Times',
      'Razorpay / Stripe Payment Engines',
      'Custom Admin Dashboards & CRM Sync',
      'API & Google Sheets Automation'
    ]
  },
];

const STATS = [
  { val: 200, suffix: '+', label: 'Events Covered' },
  { val: 50,  suffix: '+', label: 'Weddings Shot' },
  { val: 100, suffix: '+', label: 'Brands Built' },
  { val: 5,   suffix: '+', label: 'Years in Business' },
];

const MARQUEE_ITEMS = [
  'FLEX PRINTING', 'VISITING CARDS', 'BROCHURE PRINTING', 'BANNER PRINTING',
  'DIGITAL MARKETING', 'WEDDING PHOTOGRAPHY', 'LIVE STREAMING',
  'WEB DEVELOPMENT', 'BRAND IDENTITY', 'META ADS', 'EVENT COVERAGE',
  'STATIONERY DESIGN', 'GOOGLE SEO', 'VIDEOGRAPHY',
];

const WHY_ITEMS = [
  { icon: 'fa-solid fa-location-dot', title: 'Local & On-Ground', desc: 'We are physically present in Kalarcode, Alappuzha — not a remote agency. Walk in anytime.' },
  { icon: 'fa-solid fa-layer-group',  title: 'Everything Under One Roof', desc: 'Print, shoot, design, and market — all five departments available at a single address.' },
  { icon: 'fa-solid fa-chart-simple', title: 'Results-Oriented', desc: 'Every campaign, design, and strategy is tracked and optimised for measurable outcomes.' },
  { icon: 'fa-solid fa-handshake',    title: 'Long-Term Partners', desc: "We don't sell packages. We build partnerships. 95% of our clients renew every year." },
];

/* ── Component ───────────────────────────────────────────── */
const Home = () => {
  const [activeDept, setActiveDept] = useState(0);

  // Parallax on hero orb
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const orbY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <>
      <SEO
        title="Flex Printing & Digital Marketing Agency in Alappuzha | Grandframe"
        description="Best flex printing, banner printing, visiting card printing & digital marketing agency in Alappuzha, Kerala. Walk-in studio at Kalarcode for same-day flex banners, brochures, stationery & all printing services."
        keywords="flex printing Alappuzha, flex banner printing near me, visiting card printing Alappuzha, brochure printing Kerala, banner printing Kalarcode, digital marketing agency Alappuzha, printing services near me, flex board printing Alappuzha, Grandframe"
        url="https://www.grandframe.in/"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://www.grandframe.in/#website",
            "url": "https://www.grandframe.in/",
            "name": "Grandframe Creative Digital",
            "description": "Alappuzha's leading flex printing, banner printing, and digital marketing studio at Kalarcode. Printing services, photography, branding and web development.",
            "publisher": { "@id": "https://www.grandframe.in/#organization" }
          },
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://www.grandframe.in/#webpage",
            "url": "https://www.grandframe.in/",
            "name": "Flex Printing, Banner Printing & Digital Marketing in Alappuzha | Grandframe Creative Digital",
            "isPartOf": { "@id": "https://www.grandframe.in/#website" },
            "about": { "@id": "https://www.grandframe.in/#organization" },
            "breadcrumb": { "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.grandframe.in/" }] }
          }
        ]}
      />

      {/* ══════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════ */}
      <section id="home" ref={heroRef} style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: '100px',
      }}>

        {/* Background orbs */}
        <motion.div style={{ y: orbY, position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(255,92,0,0.12) 0%, transparent 65%)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', bottom: '0%', left: '-10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(10,143,220,0.08) 0%, transparent 65%)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', top: '40%', left: '30%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 65%)', borderRadius: '50%' }} />
        </motion.div>

        {/* Subtle grid lines */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        <motion.div style={{ opacity: heroOpacity }} className="container" style2={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto', textAlign: 'center', padding: '0 1rem' }}>

            {/* Location pill */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 1.1rem', borderRadius: '50px', background: 'rgba(255,92,0,0.08)', border: '1px solid rgba(255,92,0,0.2)', marginBottom: '2rem' }}
            >
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FF5C00', boxShadow: '0 0 8px rgba(255,92,0,0.8)', display: 'inline-block', animation: 'pulse 2s infinite' }}></span>
              <span style={{ color: '#FF7A2F', fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.06em' }}>Kalarcode, Alappuzha — Walk-in Studio</span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              style={{ fontFamily: 'var(--font-head)', fontSize: 'clamp(2.4rem, 6vw, 5rem)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.03em', marginBottom: '1.5rem', color: '#F8FAFC' }}
            >
              Flex Printing &amp; Creative Studio{' '}
              <span style={{ background: 'linear-gradient(135deg, #FF5C00, #FF9A5C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                in Alappuzha
              </span>
              <br />
              <span style={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.65em' }}>
                Printing · Digital Marketing · Photography · Branding · Web
              </span>
            </motion.h1>

            {/* Sub-description */}
            <motion.p
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              style={{ color: '#64748B', fontSize: 'clamp(1rem, 2vw, 1.2rem)', lineHeight: 1.75, maxWidth: '680px', margin: '0 auto 2.5rem' }}
            >
              Flex banners, visiting cards, brochures, stationery printing — plus digital marketing, photography &amp; web development. Walk into our Kalarcode, Alappuzha studio for all your printing needs.
            </motion.p>

            {/* CTA row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}
              style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '3rem' }}
            >
              <Link to="/services" className="btn btn-primary theme-btn" style={{ padding: '0.9rem 2rem', fontSize: '1rem' }}>
                Explore All Services <i className="fa-solid fa-arrow-right"></i>
              </Link>
              <a
                href="https://wa.me/919562001221?text=Hi%2C%20I%20want%20to%20know%20more%20about%20your%20services"
                target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.9rem 2rem', borderRadius: '50px', background: '#25D366', color: '#fff', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '1rem', textDecoration: 'none', boxShadow: '0 4px 18px rgba(37,211,102,0.25)', transition: 'transform 0.2s ease' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <i className="fa-brands fa-whatsapp"></i> WhatsApp Us
              </a>
            </motion.div>

            {/* Department pills */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}
            >
              {DEPARTMENTS.map(d => (
                <Link key={d.to} to={d.to} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.4rem 0.9rem', borderRadius: '50px',
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${d.color}30`,
                  color: '#94A3B8', fontSize: '0.82rem', fontWeight: 600,
                  textDecoration: 'none', transition: 'all 0.2s ease',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${d.color}12`; e.currentTarget.style.color = d.color; e.currentTarget.style.borderColor = `${d.color}60`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.borderColor = `${d.color}30`; }}
                >
                  <i className={d.icon} style={{ fontSize: '0.75rem', color: d.color }}></i>
                  {d.label}
                </Link>
              ))}
            </motion.div>

          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
          style={{ position: 'absolute', bottom: '2.5rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', zIndex: 2 }}
        >
          <span style={{ color: '#475569', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
            style={{ width: '1px', height: '32px', background: 'linear-gradient(to bottom, #FF5C00, transparent)' }}
          />
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════
          MARQUEE TICKER
      ══════════════════════════════════════════════ */}
      <div className="marquee-container" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <motion.div
          className="marquee-track"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ repeat: Infinity, ease: 'linear', duration: 28 }}
        >
          {[1, 2].map((_, i) => (
            <React.Fragment key={i}>
              {MARQUEE_ITEMS.map((item, j) => (
                <span key={j} className="marquee-item">
                  <i className="fa-solid fa-diamond" style={{ color: 'var(--color-orange)', fontSize: '0.5rem', marginRight: '10px' }}></i> {item}
                </span>
              ))}
            </React.Fragment>
          ))}
        </motion.div>
      </div>

      {/* ══════════════════════════════════════════════
          DEPARTMENTS GRID
      ══════════════════════════════════════════════ */}
      <section className="container section-padding">
        <motion.div
          className="text-center" style={{ marginBottom: '3.5rem' }}
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
        >
          <span className="sub-title">What We Do</span>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 800, marginTop: '0.5rem' }}>
            Five Departments. One Address.
          </h2>
          <p style={{ color: '#64748B', maxWidth: '580px', margin: '1rem auto 0', lineHeight: 1.75 }}>
            Whether you need a wedding filmed, banners printed, or a Google Ads campaign launched — we handle it all in-house at our studio, with zero outsourcing.
          </p>
        </motion.div>

        <div className="dept-showcase-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem', alignItems: 'stretch' }}>
          
          {/* Left: Department List Selector */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {DEPARTMENTS.map((dept, idx) => {
              const isActive = activeDept === idx;
              return (
                <div
                  key={dept.label}
                  onClick={() => setActiveDept(idx)}
                  onMouseEnter={() => setActiveDept(idx)}
                  className="glass-panel"
                  style={{
                    padding: '1.25rem 1.5rem',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    border: `1px solid ${isActive ? dept.color : 'rgba(255,255,255,0.06)'}`,
                    background: isActive ? `${dept.color}08` : 'rgba(255,255,255,0.02)',
                    transition: 'all 0.25s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={e => {
                    if (!isActive) e.currentTarget.style.borderColor = `${dept.color}30`;
                  }}
                  onMouseLeave={e => {
                    if (!isActive) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                  }}
                >
                  {/* Active left indicator line */}
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '3px',
                    background: dept.color,
                    opacity: isActive ? 1 : 0,
                    transition: 'opacity 0.2s ease'
                  }} />

                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: `${dept.color}12`,
                      border: `1px solid ${dept.color}25`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <i className={dept.icon} style={{ color: dept.color, fontSize: '1rem' }}></i>
                    </div>
                    <div>
                      <h3 style={{
                        fontFamily: 'var(--font-head)',
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: isActive ? '#F8FAFC' : '#94A3B8',
                        margin: 0,
                        transition: 'color 0.2s'
                      }}>
                        {dept.label}
                      </h3>
                      <span style={{ fontSize: '0.78rem', color: '#64748B', display: 'block', marginTop: '0.1rem' }}>
                        Focus: {dept.focus}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: Detailed Active Department Card */}
          <div style={{ position: 'relative', minHeight: '380px' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeDept}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                style={{ height: '100%' }}
              >
                <div
                  className="glass-panel"
                  style={{
                    padding: '2.5rem',
                    borderRadius: '24px',
                    height: '100%',
                    border: `1px solid ${DEPARTMENTS[activeDept]?.color || '#FF5C00'}25`,
                    background: 'rgba(10, 18, 30, 0.4)',
                    boxShadow: `0 20px 50px ${DEPARTMENTS[activeDept]?.color || '#FF5C00'}05`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Decorative glowing gradient circle on right */}
                  <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '180px',
                    height: '180px',
                    background: `radial-gradient(circle, ${DEPARTMENTS[activeDept]?.color || '#FF5C00'}12 0%, transparent 70%)`,
                    pointerEvents: 'none'
                  }} />

                  <div>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '1.25rem' }}>
                      <span style={{
                        padding: '0.35rem 0.9rem',
                        borderRadius: '50px',
                        background: `${DEPARTMENTS[activeDept]?.color || '#FF5C00'}12`,
                        border: `1px solid ${DEPARTMENTS[activeDept]?.color || '#FF5C00'}25`,
                        color: DEPARTMENTS[activeDept]?.color || '#FF5C00',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {DEPARTMENTS[activeDept]?.focus}
                      </span>
                    </div>

                    <h2 style={{
                      fontFamily: 'var(--font-head)',
                      fontSize: '1.75rem',
                      fontWeight: 800,
                      color: '#F8FAFC',
                      marginBottom: '1rem'
                    }}>
                      {DEPARTMENTS[activeDept]?.label}
                    </h2>

                    <p style={{
                      color: '#94A3B8',
                      fontSize: '0.95rem',
                      lineHeight: 1.7,
                      marginBottom: '2rem'
                    }}>
                      {DEPARTMENTS[activeDept]?.desc}
                    </p>

                    {/* Deliverables checklist */}
                    <div style={{ marginBottom: '2.5rem' }}>
                      <h4 style={{
                        fontFamily: 'var(--font-head)',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        color: '#64748B',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: '1rem'
                      }}>
                        Core Offerings & Deliverables
                      </h4>
                      <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '0.75rem'
                      }}>
                        {DEPARTMENTS[activeDept]?.deliverables?.map((item, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                            <i className="fa-solid fa-circle-check" style={{ color: DEPARTMENTS[activeDept]?.color || '#FF5C00', marginTop: '4px', fontSize: '0.85rem' }}></i>
                            <span style={{ color: '#CBD5E1', fontSize: '0.9rem', lineHeight: 1.4 }}>
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* CTA Footer */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    paddingTop: '1.5rem',
                    borderTop: '1px solid rgba(255,255,255,0.06)'
                  }}>
                    {/* Tag list */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {DEPARTMENTS[activeDept]?.tags?.map(tag => (
                        <span key={tag} style={{
                          padding: '0.2rem 0.6rem',
                          borderRadius: '50px',
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          color: '#64748B',
                          fontSize: '0.72rem',
                          fontWeight: 600
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>

                    <Link
                      to={DEPARTMENTS[activeDept]?.to || '/services'}
                      className="btn btn-primary theme-btn"
                      style={{
                        padding: '0.75rem 1.5rem',
                        fontSize: '0.88rem',
                        background: `linear-gradient(135deg, ${DEPARTMENTS[activeDept]?.color || '#FF5C00'}, ${DEPARTMENTS[activeDept]?.color || '#FF5C00'}bb)`
                      }}
                    >
                      Explore Department <i className="fa-solid fa-arrow-right"></i>
                    </Link>
                  </div>

                </div>
              </motion.div>
            </AnimatePresence>
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════
          STATS BAR
      ══════════════════════════════════════════════ */}
      <section style={{ padding: '0 0 6rem' }}>
        <motion.div
          className="container"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
        >
          <div className="glass-panel" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0', borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(255,92,0,0.12)' }}>
            {STATS.map((s, i) => (
              <div key={s.label} style={{ padding: '2.5rem 1.5rem', textAlign: 'center', borderRight: i < STATS.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', position: 'relative' }}>
                <div style={{ fontFamily: 'var(--font-head)', fontSize: 'clamp(2.2rem, 4vw, 3rem)', fontWeight: 900, color: '#F8FAFC', lineHeight: 1, marginBottom: '0.5rem' }}>
                  <AnimatedCounter target={s.val} />{s.suffix}
                </div>
                <div style={{ color: '#475569', fontSize: '0.875rem', fontWeight: 500 }}>{s.label}</div>
                {i === 0 && <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '120px', height: '120px', background: 'radial-gradient(circle, rgba(255,92,0,0.05) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />}
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════
          WHY GRANDFRAME
      ══════════════════════════════════════════════ */}
      <section style={{ padding: '0 0 6rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '50%', left: '-10%', transform: 'translateY(-50%)', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(255,92,0,0.04) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div className="text-center" style={{ marginBottom: '3.5rem' }} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="sub-title">Why Choose Us</span>
            <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 800, marginTop: '0.5rem' }}>
              Why Grandframe?
            </h2>
          </motion.div>

          <motion.div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem' }}
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={staggerContainer}
          >
            {WHY_ITEMS.map(item => (
              <motion.div key={item.title} variants={fadeUp} className="glass-panel" style={{ padding: '2rem', borderRadius: '18px', borderLeft: '3px solid rgba(255,92,0,0.3)' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(255,92,0,0.08)', border: '1px solid rgba(255,92,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                  <i className={item.icon} style={{ color: '#FF5C00', fontSize: '1.1rem' }}></i>
                </div>
                <h3 style={{ fontFamily: 'var(--font-head)', fontSize: '1.05rem', fontWeight: 700, color: '#F1F5F9', marginBottom: '0.6rem' }}>{item.title}</h3>
                <p style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: 1.65, margin: 0 }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          IN-PERSON VISIT CTA STRIP
      ══════════════════════════════════════════════ */}
      <section className="container" style={{ paddingBottom: '4rem' }}>
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2rem', padding: '2rem 2.5rem', borderRadius: '20px', background: 'rgba(255,92,0,0.06)', border: '1px solid rgba(255,92,0,0.18)', flexWrap: 'wrap' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'rgba(255,92,0,0.12)', border: '1px solid rgba(255,92,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="fa-solid fa-store" style={{ color: '#FF5C00', fontSize: '1.3rem' }}></i>
            </div>
            <div>
              <h4 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, color: '#F1F5F9', fontSize: '1rem', marginBottom: '0.2rem' }}>Walk into our studio anytime</h4>
              <p style={{ color: '#64748B', fontSize: '0.88rem', margin: 0 }}>
                Kaithavana, AC Highway, Kalarcode, Alappuzha · Open Mon–Sat, 9 AM–6 PM
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <a href="https://wa.me/919562001221" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.7rem 1.4rem', borderRadius: '50px', background: '#25D366', color: '#fff', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none', boxShadow: '0 4px 12px rgba(37,211,102,0.25)', transition: 'transform 0.2s ease' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
              <i className="fa-brands fa-whatsapp"></i> +91 9562 001221
            </a>
            <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.7rem 1.4rem', borderRadius: '50px', background: 'transparent', color: '#CBD5E1', fontFamily: 'var(--font-head)', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)', transition: 'border-color 0.2s ease, color 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,92,0,0.4)'; e.currentTarget.style.color = '#FF7A2F'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#CBD5E1'; }}>
              Get Directions <i className="fa-solid fa-map-location-dot"></i>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════════════ */}
      <section className="cta section-padding">
        <motion.div
          className="container text-center"
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={fadeUp}
        >
          <div className="cta-box glass-panel" style={{ position: 'relative', overflow: 'hidden', borderRadius: '28px', padding: '5rem 3rem', border: '1px solid rgba(255,92,0,0.15)' }}>
            {/* Ambient glows */}
            <div style={{ position: 'absolute', top: '-60px', left: '-60px', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(255,92,0,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-60px', right: '-60px', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(10,143,220,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ relative: 'relative', zIndex: 1 }}>
              <span style={{ display: 'inline-block', padding: '0.3rem 0.9rem', borderRadius: '50px', background: 'rgba(255,92,0,0.1)', border: '1px solid rgba(255,92,0,0.25)', color: '#FF7A2F', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
                Free Consultation
              </span>
              <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 'clamp(1.8rem, 4vw, 3.2rem)', fontWeight: 900, lineHeight: 1.2, marginBottom: '1rem' }}>
                Ready to Grow Your Business?
              </h2>
              <p style={{ color: '#64748B', maxWidth: '520px', margin: '0 auto 2.5rem', lineHeight: 1.75, fontSize: '1.05rem' }}>
                Talk to our team about your goals. No pressure, no jargon — just an honest conversation about what will actually work for your business.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn btn-primary theme-btn" style={{ padding: '0.9rem 2rem' }}>
                  Contact Us <i className="fa-solid fa-arrow-right"></i>
                </Link>
                <Link to="/pricing" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.9rem 2rem', borderRadius: '50px', color: '#CBD5E1', fontFamily: 'var(--font-head)', fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)', transition: 'all 0.2s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,92,0,0.4)'; e.currentTarget.style.color = '#FF7A2F'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#CBD5E1'; }}>
                  See Pricing <i className="fa-solid fa-tags"></i>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Pulse animation keyframe */}
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,92,0,0.5); }
          50% { box-shadow: 0 0 0 6px rgba(255,92,0,0); }
        }
      `}</style>
    </>
  );
};

export default Home;
